import { Observable, catchError, combineLatest, map, of } from 'rxjs';
import { min } from '@akropolis-web/primitives';
import type { LiquidityAmount, PercentAmount, Token } from '@akropolis-web/primitives';

import { protocolsApi, ratingsApi } from 'api/modules';
import {
  calculateGrowth,
  getMarketTokens,
  getWorstRating,
  hasSignificantAmounts,
  hasUnknownPrice,
} from 'domain/utils';
import { combine, left, right, Either } from 'utils/either';
import { ERROR_MESSAGES } from 'domain/utils/constants';
import { calcFallbackedTotalUsd } from 'api/utils/calcFallbackedTotalUSD';
import {
  LendingAssetTotal,
  FallbackedTotalUSD,
  Rating,
  ProductSlug,
  VersionSlug,
  NetworkSlug,
} from 'domain/types';

export type MarketsTableData = {
  name: string;
  rating: Either<Rating>;
  marketId: string;
  tokens: Token[];
  utilizationRatio: Either<PercentAmount>;
  impactCost: Either<LiquidityAmount>;
  impactCostModel: Either<string>;
  totalDebt: FallbackedTotalUSD;
  totalCollateral: FallbackedTotalUSD;
  totalDeposit: FallbackedTotalUSD;
  growth24H: Either<{
    deposit: PercentAmount | null;
    debt: PercentAmount | null;
    collateral: PercentAmount | null;
  }> | null;
  lowSupply: boolean;
  assetTotals: LendingAssetTotal[];
};

export function getMarketsTableData({
  productSlug,
  versionSlug,
  network,
}: {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
}): Observable<{ tableData: MarketsTableData[] }> {
  return combineLatest([
    protocolsApi.getLendingInfo$(true, { productSlug, versionSlug, network }),
    protocolsApi.getMarketsMetrics$('latest', { productSlug, versionSlug, network }),
    protocolsApi
      .getMarketsMetrics$('dayAgo', { productSlug, versionSlug, network })
      .pipe(catchError(() => of(null))),
    protocolsApi.getImpactCosts$({ productSlug, versionSlug, network }),
    ratingsApi.getMarketsRatings$({ productSlug, versionSlug, network }),
  ]).pipe(
    map(([protocolInfo, allMarketsMetrics, allMetricsDayAgo, impactCosts, marketsRatings]) => {
      return {
        tableData: allMarketsMetrics.map(metrics => {
          const minImpactCost = impactCosts.byMarketVid[metrics.marketId];

          const market = protocolInfo.marketByVid[metrics.marketId];
          const metricsDayAgo =
            allMetricsDayAgo?.find(x => x.marketId === metrics.marketId) || null;
          const significantGrowth = 0.01;
          const tokens = getMarketTokens({ protocolInfo, marketId: metrics.marketId });
          const withUnknownPrice = hasUnknownPrice(metrics.assetTotals);

          const marketRating = getWorstRating(marketsRatings.get(metrics.marketId) || []);

          const growth24H: MarketsTableData['growth24H'] = withUnknownPrice
            ? left(ERROR_MESSAGES.NOT_ALL_TOKENS_HAS_PRICE)
            : metricsDayAgo &&
              combine(metricsDayAgo.balanceUSD, metrics.balanceUSD).map(
                ([balanceUsdDayAgo, balanceUSD]) => ({
                  debt:
                    calculateGrowth(
                      balanceUSD.totalDebt,
                      balanceUsdDayAgo.totalDebt,
                      significantGrowth,
                    )?.percent || null,
                  collateral:
                    calculateGrowth(
                      balanceUSD.totalCollateral,
                      balanceUsdDayAgo.totalCollateral,
                      significantGrowth,
                    )?.percent || null,
                  deposit:
                    calculateGrowth(
                      balanceUsdDayAgo.totalDeposit,
                      balanceUSD.totalDeposit,
                      significantGrowth,
                    )?.percent || null,
                }),
              );

          const data: MarketsTableData = {
            ...metrics,
            tokens,
            rating: marketRating.map(r => r.rating),
            marketId: metrics.marketId,
            name: market?.marketName || 'Unknown market',
            utilizationRatio: metrics.utilizationRatio,
            impactCost: minImpactCost
              ? right(min(minImpactCost.pumpCost, minImpactCost.dumpCost))
              : left(ERROR_MESSAGES.UNKNOWN_IMPACT_DATA),
            impactCostModel: minImpactCost
              ? right(minImpactCost.model)
              : left(ERROR_MESSAGES.UNKNOWN_IMPACT_DATA),
            totalDebt: calcFallbackedTotalUsd(metrics.assetTotals, 'totalDebt', true),
            totalCollateral: calcFallbackedTotalUsd(metrics.assetTotals, 'totalCollateral', true),
            totalDeposit: calcFallbackedTotalUsd(metrics.assetTotals, 'totalDeposit', true),
            growth24H,
            lowSupply: metrics.assetTotals.every(m => !hasSignificantAmounts(m.balance)),
          };
          return data;
        }),
      };
    }),
  );
}
